import React, { useCallback, useEffect, useState } from 'react'
import Alert from 'react-bootstrap/Alert'
import Form from 'react-bootstrap/Form'
import Pagination from 'react-bootstrap/Pagination'
import Helmet from 'react-helmet'
import { useLocation } from 'react-router-dom'
import { Element, scroller } from 'react-scroll'

import { useDebouncedCallback } from 'use-debounce'

import { CaseResultCard } from '../components/case-search/CaseResultCard'
import { CaseResultRow } from '../components/case-search/CaseResultRow.js'
import { CaseSearchColumHeader } from '../components/case-search/CaseSearchColumHeader.js'
import { getReportStatusDisplay } from '../components/case-search/caseStatusHelpers'
import { SearchBox } from '../components/case-search/SearchBox.js'
import { SkeletonRow } from '../components/common/SkeletonRow'
import FAIcon from '../components/FAIcon'
import getErrorMessage from '../services/api/getErrorMessage'
import caseSearch from '../services/caseSearch'
import { nextSort } from '../utils/nextSort.js'
import { paginate } from '../utils/paginate.js'

import './Cases.scss'

/** @typedef { import('../services/types/useState.d.ts') } */
/** @typedef { import('../services/types/CaseSearchInput').CaseSearchInput } CaseSearchInput */
/** @typedef { import('../services/types/CaseSearchResultItem').CaseSearchResult } CaseSearchResult */

export default function Cases({ settings }) {
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState('')
  const { key: locationKey } = useLocation()

  /** @type {useState<CaseSearchResult>} */
  const [cases, setCases] = useState()
  const [viewMode, setViewMode] = useState(() => {
    try {
      return window.sessionStorage.getItem('caseList_viewMode') || 'list'
    } catch {
      return 'list'
    }
  })

  const [showClosed, setShowClosed] = useState(() => {
    try {
      const saved = window.sessionStorage.getItem('caseList_showClosed')
      return saved === 'true'
    } catch {
      return false
    }
  })

  /** @type {useState<CaseSearchInput>} */
  const [caseSearchInput, setCaseSearchInput] = useState(() => {
    try {
      const saved = window.sessionStorage.getItem('caseList_searchInput')
      if (saved) return JSON.parse(saved)
    } catch {
      // ignore
    }
    return {
      orderBy: {
        ClientLastName: 'Asc',
      },
      page: 1,
      perPage: 20,
    }
  })

  useEffect(() => {
    const abortController = new AbortController()

    caseSearch(caseSearchInput, abortController.signal)
      .then(setCases)
      .catch(err => {
        if (err.name === 'AbortError') return
        setError(getErrorMessage(err))
      })
      .finally(() => {
        if (abortController.signal.aborted) return
        setIsLoading(false)
        scroller.scrollTo('search-container', {
          duration: 800,
          delay: 0,
          smooth: 'easeInOutQuart',
        })
      })

    return () => {
      abortController.abort()
    }
  }, [caseSearchInput, locationKey])

  const handleSearch = searchInput => {
    setCaseSearchInput(prev => {
      const next = typeof searchInput === 'function' ? searchInput(prev) : searchInput
      try {
        window.sessionStorage.setItem('caseList_searchInput', JSON.stringify(next))
      } catch {
        // ignore storage errors
      }
      return next
    })
    setIsLoading(true)
  }

  const handleViewModeChange = mode => {
    setViewMode(mode)
    try {
      window.sessionStorage.setItem('caseList_viewMode', mode)
    } catch {
      // ignore
    }
  }

  const setPage = useCallback(page => {
    return () => {
      handleSearch(s => ({ ...s, page }))
    }
  }, [])

  const toggleSort = column => {
    return e => {
      e.preventDefault()
      handleSearch(s => ({
        ...s,
        orderBy: {
          [column]: nextSort(s.orderBy?.[column]),
        },
      }))
    }
  }

  const setSearchText = searchText => {
    handleSearch(s => ({ ...s, searchText, page: 1 }))
  }

  const handleReportStatusFilterChange = e => {
    const reportStatus = e.target.value || undefined
    handleSearch(s => ({ ...s, reportStatus, page: 1 }))
  }

  const debouncedSetSearchText = useDebouncedCallback(setSearchText, 500, { maxWait: 2000 })

  const hasSearchResults = cases?.results?.length > 0

  const { orderBy = {} } = caseSearchInput

  const pagingItems = hasSearchResults
    ? paginate(cases.page, cases.totalPages).map((number, index) => {
        if (number === '…') {
          return <Pagination.Ellipsis key={number + index} disabled />
        }
        return (
          <Pagination.Item key={number} active={number === cases.page} onClick={setPage(number)}>
            {number}
          </Pagination.Item>
        )
      })
    : []

  const handleShowCasesToggle = () => {
    setShowClosed(s => {
      const newValue = !s
      try {
        window.sessionStorage.setItem('caseList_showClosed', newValue)
      } catch (e) {
        // ignore storage errors
      }
      return newValue
    })
  }

  useEffect(() => {
    handleSearch(s => ({ ...s, showClosed, page: 1 }))
  }, [showClosed])

  const submittedLabel = getReportStatusDisplay('submitted', settings) || 'Sent to Administrator'
  const inProgressLabel = getReportStatusDisplay('in-progress', settings) || 'In Progress'
  const approvedLabel = getReportStatusDisplay('approved', settings) || 'Saved and Completed'
  const filedLabel = getReportStatusDisplay('filed', settings) || 'Filed'
  const invoicedLabel = getReportStatusDisplay('invoiced', settings) || 'Invoiced'

  return (
    <div className="cases-list-container pb-3">
      <Helmet title="Cases" />
      <Element name="scroll-to-container" className="scroll-to-container" />
      {error && (
        <Alert variant="danger">
          <strong>Error:</strong> {error.message}
        </Alert>
      )}
      <div className="cases-toolbar d-flex flex-wrap my-2 align-items-center">
        <SearchBox
          className="d-flex flex-grow-1 mr-md-2 mb-2 mb-md-0"
          onChange={debouncedSetSearchText}
          onSubmit={setSearchText}
          placeholder="Search by claimant, client, or member name"
          initialValue={caseSearchInput.searchText ?? ''}
        />
        <div className="d-flex flex-wrap align-items-center cases-toolbar__controls">
          <Form.Control
            as="select"
            size="sm"
            className="cases-report-filter-select mr-2"
            value={caseSearchInput.reportStatus || ''}
            onChange={handleReportStatusFilterChange}
            aria-label="Filter by report status"
            title="Filter cases by priority report status"
          >
            <option value="">All Report Statuses</option>
            <option value="open">Open Reports (All)</option>
            <option value="submitted">&nbsp;&nbsp;— {submittedLabel}</option>
            <option value="in_smart_review">&nbsp;&nbsp;— In Smart Review</option>
            <option value="in-progress">&nbsp;&nbsp;— {inProgressLabel}</option>
            <option value="closed">Closed Reports (All)</option>
            <option value="approved">&nbsp;&nbsp;— {approvedLabel}</option>
            <option value="filed">&nbsp;&nbsp;— {filedLabel}</option>
            <option value="invoiced">&nbsp;&nbsp;— {invoicedLabel}</option>
            <option value="draft">Draft</option>
          </Form.Control>

          <label className="closed-cases-checkbox mr-3">
            Show Closed Cases <input type="checkbox" onChange={handleShowCasesToggle} checked={showClosed} />
          </label>

          <div className="btn-group btn-group-sm" role="group" aria-label="View mode">
            <button
              type="button"
              className={`btn ${viewMode === 'list' ? 'btn-primary' : 'btn-light text-muted border'}`}
              onClick={() => handleViewModeChange('list')}
              title="List View"
            >
              <FAIcon name="list" />
            </button>
            <button
              type="button"
              className={`btn ${viewMode === 'grid' ? 'btn-primary' : 'btn-light text-muted border'}`}
              onClick={() => handleViewModeChange('grid')}
              title="Card View"
            >
              <FAIcon name="th-large" />
            </button>
          </div>
        </div>
      </div>

      {viewMode === 'list' ? (
        <div className="card border-light">
          <table className="table mb-0">
            <thead>
              <tr>
                <CaseSearchColumHeader
                  label="CLAIMANT"
                  sortable
                  sortDirection={orderBy.ClientLastName}
                  onClick={toggleSort('ClientLastName')}
                />
                <CaseSearchColumHeader label="MEMBERS" className="d-none d-md-table-cell" />
                <CaseSearchColumHeader
                  label="CLIENT"
                  className="d-none d-xl-table-cell"
                  sortable
                  sortDirection={orderBy.Company}
                  onClick={toggleSort('Company')}
                />
                <CaseSearchColumHeader
                  label="OPENED ON"
                  className="d-none d-sm-table-cell"
                  sortable
                  sortDirection={orderBy.CreatedUtc}
                  onClick={toggleSort('CreatedUtc')}
                />
                <CaseSearchColumHeader
                  label="CASE TYPE"
                  className="d-none d-lg-table-cell"
                  sortable
                  sortDirection={orderBy.CaseTypeName}
                  onClick={toggleSort('CaseTypeName')}
                />
                <CaseSearchColumHeader
                  label="CASE STATUS"
                  id="case-status-column-header"
                  sortable
                  sortDirection={orderBy.Status}
                  onClick={toggleSort('Status')}
                />
                <CaseSearchColumHeader
                  label="REPORT STATUS"
                  id="status-column-header"
                  className="d-none d-sm-table-cell"
                  sortable
                  sortDirection={orderBy.ReportStatus}
                  onClick={toggleSort('ReportStatus')}
                />
                <CaseSearchColumHeader
                  label="LAST UPDATED"
                  className="d-none d-lg-table-cell"
                  sortable
                  sortDirection={orderBy.LastUpdatedUtc}
                  onClick={toggleSort('LastUpdatedUtc')}
                />
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <>
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                  <SkeletonRow />
                </>
              ) : (
                hasSearchResults &&
                cases.results.map(rowData => {
                  return (
                    <CaseResultRow
                      key={rowData.caseNumber}
                      rowData={rowData}
                      onMemberClick={setSearchText}
                      settings={settings}
                    />
                  )
                })
              )}
            </tbody>
          </table>
          {!isLoading && !hasSearchResults && (
            <div className="empty-table-message-container">
              <h6 className="mb-0">No cases have been assigned</h6>
            </div>
          )}
        </div>
      ) : (
        <div className="cases-card-grid mt-2">
          {isLoading ? (
            <div className="row">
              {[1, 2, 3, 4, 5, 6].map(n => (
                <div key={n} className="col-xl-4 col-md-6 mb-3">
                  <div className="card h-100 shadow-sm p-3">
                    <div className="skeleton mb-2" style={{ width: '60%', height: '1.2em' }} />
                    <div className="skeleton mb-3" style={{ width: '40%', height: '0.9em' }} />
                    <div className="skeleton mb-2" style={{ width: '80%', height: '0.9em' }} />
                    <div className="skeleton mt-auto pt-2" style={{ width: '100%', height: '1.5em' }} />
                  </div>
                </div>
              ))}
            </div>
          ) : hasSearchResults ? (
            <div className="row">
              {cases.results.map(rowData => (
                <CaseResultCard
                  key={rowData.caseNumber}
                  rowData={rowData}
                  onMemberClick={setSearchText}
                  settings={settings}
                />
              ))}
            </div>
          ) : (
            <div className="card border-light">
              <div className="empty-table-message-container">
                <h6 className="mb-0">No cases have been assigned</h6>
              </div>
            </div>
          )}
        </div>
      )}

      {hasSearchResults && (
        <div>
          <div className="text-right mt-3">
            <small>
              Showing {cases.results.length} of {cases.total} cases
            </small>
          </div>
          <div className="d-flex align-content-center">
            <Pagination>
              <Pagination.First onClick={setPage(1)} disabled={cases.page === 1} />
              <Pagination.Prev onClick={setPage(cases.page - 1)} disabled={cases.hasPrevious === false} />
              {pagingItems}
              <Pagination.Next onClick={setPage(cases.page + 1)} disabled={cases.hasNext === false} />
              <Pagination.Last onClick={setPage(cases.totalPages)} disabled={cases.page === cases.totalPages} />
            </Pagination>
          </div>
        </div>
      )}
    </div>
  )
}
